/// <reference types="google.maps" />
// POI search input for the manual route builder — searches any place (monuments,
// restaurants, addresses…) biased around the chosen city and returns full info.
import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/google-maps-loader";
import { getPlacePhoto } from "@/lib/maps.functions";

export type PoiResult = {
  name: string;
  lat: number;
  lng: number;
  placeId?: string;
  address?: string;
  photoUrl?: string;
};

type Suggestion = {
  placeId: string;
  primary: string;
  secondary: string;
};

export function StopSearchInput({
  center,
  onAdd,
  placeholder,
  language = "en",
  className,
}: {
  center: { lat: number; lng: number };
  onAdd: (p: PoiResult) => void;
  placeholder?: string;
  language?: string;
  className?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const placesLibRef = useRef<google.maps.PlacesLibrary | null>(null);
  const debounceRef = useRef<number | null>(null);
  const reqIdRef = useRef(0);
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(language)
      .then(async () => {
        const lib = (await google.maps.importLibrary("places")) as google.maps.PlacesLibrary;
        if (cancelled) return;
        placesLibRef.current = lib;
        sessionRef.current = new lib.AutocompleteSessionToken();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [language]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchSuggestions = (input: string) => {
    const lib = placesLibRef.current;
    if (!lib || !input.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const myReq = ++reqIdRef.current;
    lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      sessionToken: sessionRef.current ?? undefined,
      locationBias: { center: { lat: center.lat, lng: center.lng }, radius: 30000 },
      language,
    })
      .then(({ suggestions: sugs }) => {
        if (myReq !== reqIdRef.current) return;
        const mapped: Suggestion[] = [];
        for (const s of sugs) {
          const p = s.placePrediction;
          if (!p?.placeId) continue;
          mapped.push({
            placeId: p.placeId,
            primary: p.mainText?.toString() ?? p.text?.toString() ?? "",
            secondary: p.secondaryText?.toString() ?? "",
          });
        }
        setSuggestions(mapped.slice(0, 6));
        setOpen(mapped.length > 0);
        setActiveIdx(-1);
      })
      .catch(() => {
        setSuggestions([]);
      });
  };

  const handleChange = (v: string) => {
    setValue(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => fetchSuggestions(v), 200);
  };

  const selectSuggestion = async (s: Suggestion) => {
    const lib = placesLibRef.current;
    if (!lib) return;
    try {
      const place = new lib.Place({ id: s.placeId });
      await place.fetchFields({ fields: ["location", "displayName", "formattedAddress", "photos"] });
      const loc = place.location;
      if (!loc) return;
      let photoUrl: string | undefined;
      try {
        // Prefer server-resolved CDN URL (no API key / referrer needed, works
        // on custom domains). Falls back to client-side getURI() if it fails.
        const { photoUrl: serverUrl } = await getPlacePhoto({ data: { placeId: s.placeId } });
        if (serverUrl) {
          photoUrl = serverUrl;
        } else {
          photoUrl = place.photos?.[0]?.getURI({ maxWidth: 800 }) ?? undefined;
        }
      } catch {
        try {
          photoUrl = place.photos?.[0]?.getURI({ maxWidth: 800 }) ?? undefined;
        } catch {
          /* ignore */
        }
      }
      onAdd({
        name: place.displayName ?? s.primary,
        lat: loc.lat(),
        lng: loc.lng(),
        placeId: s.placeId,
        address: place.formattedAddress ?? s.secondary,
        photoUrl,
      });
      setValue("");
      setOpen(false);
      setSuggestions([]);
      sessionRef.current = new lib.AutocompleteSessionToken();
    } catch {
      /* ignore */
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectSuggestion(suggestions[activeIdx >= 0 ? activeIdx : 0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative flex-1">
      <input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        aria-label="Search place to add"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-auto rounded-xl border border-border bg-popover text-popover-foreground shadow-lg"
          role="listbox"
        >
          {suggestions.map((s, idx) => (
            <li key={s.placeId}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(s);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left text-sm transition ${
                  activeIdx === idx ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
                }`}
              >
                <span className="font-medium">{s.primary}</span>
                {s.secondary && <span className="text-xs text-muted-foreground">{s.secondary}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
